import React, { useRef, useState } from 'react';

import { Box, Button, Paper, Typography, alpha } from '@mui/material';

import { BoxResizeHandle } from '@@common';

import useIsMobile from '../../common/hooks/useIsMobile';
import { appShape } from '../../themes/shapeTokens';
import { CodeGenArtifactContext, useCodeGenChatAdapter } from '../../views/ContextDesigner/Hooks/useCodeGenChatAdapter';
import PromptChat from './PromptChat';
import { ChatMessage, PromptChatAdapter } from './PromptChatAdapter';

const DEFAULT_WIDTH = 380;
const MIN_WIDTH = 260;
const MAX_WIDTH = 620;

export type ChatTargetType = 'Operation' | 'EventHandler';

export interface PromptChatPanelProps {
	contextId?: string;
	adapter?: PromptChatAdapter;
	area?: string;
	defaultTitle?: string;
	targetType?: ChatTargetType;
	targetId?: string;
	artifactContext?: CodeGenArtifactContext;
	storageKey?: string;
	emptyStateText?: string;
	suggestedPrompts?: string[];
	onComplete?: () => void | Promise<void>;
	onAssistantMessageReceived?: (message: ChatMessage) => void | Promise<void>;
	mobileCollapsible?: boolean;
	disableDesktopVerticalInset?: boolean;
}

function emptyStateFrom(targetType?: ChatTargetType): string {
	if (targetType === 'EventHandler') return 'Ask me anything about this event handler.';
	if (targetType === 'Operation') return 'Ask me anything about this operation.';
	return 'Ask me anything about this domain.';
}

function suggestedPromptsFrom(targetType?: ChatTargetType): string[] {
	if (targetType === 'EventHandler') return ['Explain this event handler', 'List dependencies', 'Suggest a test case'];
	if (targetType === 'Operation') return ['Explain this operation', 'List dependencies', 'Suggest an implementation'];
	return ['Explain this domain', 'List dependencies', 'Suggest the next change'];
}

const PromptChatPanel: React.FC<PromptChatPanelProps> = ({
	contextId,
	adapter: suppliedAdapter,
	area,
	defaultTitle = 'Randol Service Assistant',
	targetType,
	targetId,
	artifactContext,
	storageKey = 'promptChatPanel.width',
	emptyStateText,
	suggestedPrompts,
	onComplete,
	onAssistantMessageReceived,
	mobileCollapsible = false,
	disableDesktopVerticalInset = false,
}) => {
	const [panelWidth, setPanelWidth] = useState<number>(DEFAULT_WIDTH);
	const [isChatDialogOpen, setIsChatDialogOpen] = useState(false);
	const [isMobileChatExpanded, setIsMobileChatExpanded] = useState(true);
	const panelRef = useRef<HTMLDivElement>(null);
	const isMobile = useIsMobile();
	const isMobileChatCollapsed = isMobile && mobileCollapsible && !isMobileChatExpanded;

	const inferredArtifactContext: CodeGenArtifactContext = artifactContext ?? {
		operationId: targetType === 'Operation' ? targetId : undefined,
		eventHandlerId: targetType === 'EventHandler' ? targetId : undefined,
	};
	const contextAdapter = useCodeGenChatAdapter(contextId ?? '', inferredArtifactContext);
	const adapter = suppliedAdapter ?? contextAdapter;

	return (
		<Paper
			ref={panelRef}
			elevation={0}
			sx={(theme) => {
				return {
					position: 'relative',
					width: isChatDialogOpen ? 0 : isMobile ? '100%' : panelWidth,
					flexBasis: isChatDialogOpen ? 0 : isMobile ? '100%' : panelWidth,
					minWidth: 0,
					height: isChatDialogOpen ? 0 : isMobileChatCollapsed ? 'auto' : { xs: 440, md: 'auto' },
					flexShrink: 0,
					display: 'flex',
					flexDirection: 'column',
					borderRadius: isMobile ? 0 : appShape.surfaceRadius,
					boxShadow: isMobile ? 'none' : `0 8px 28px ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.2 : 0.08)}`,
					border: isMobile ? undefined : `1px solid ${alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.18 : 0.08)}`,
					ml: 0,
					mr: !isChatDialogOpen && !isMobile ? `${theme.layout.edgeGap}px` : 0,
					my: !isChatDialogOpen && !isMobile && !disableDesktopVerticalInset ? `${theme.layout.edgeGap}px` : 0,
					minHeight: 0,
					overflow: 'hidden',
				};
			}}
		>
			{!isMobile && !isChatDialogOpen && (
				<BoxResizeHandle
					position="left"
					width={panelWidth}
					setWidth={setPanelWidth}
					panelRef={panelRef}
					storageKey={storageKey}
					defaultWidth={DEFAULT_WIDTH}
					minWidth={MIN_WIDTH}
					maxWidth={MAX_WIDTH}
					direction="right"
					hoverColor="transparent"
					draggingColor="divider"
				/>
			)}
			{isMobileChatCollapsed ? (
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 1.5, py: 1 }}>
					<Typography variant="body2" fontWeight={700}>{defaultTitle}</Typography>
					<Button size="small" onClick={() => setIsMobileChatExpanded(true)}>Open chat</Button>
				</Box>
			) : (
				<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
					<PromptChat
						adapter={adapter}
						defaultTitle={defaultTitle}
						showAgentNameInHeader
						showConversationNameBar
						area={area}
						emptyStateText={emptyStateText ?? emptyStateFrom(targetType)}
						suggestedPrompts={suggestedPrompts ?? suggestedPromptsFrom(targetType)}
						onComplete={onComplete}
						onAssistantMessageReceived={onAssistantMessageReceived}
						onDialogOpenChange={setIsChatDialogOpen}
						onDismiss={isMobile && mobileCollapsible ? () => setIsMobileChatExpanded(false) : undefined}
					/>
				</Box>
			)}
		</Paper>
	);
};

export default PromptChatPanel;
